/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  UserAllowAccess,
  UserDataToTransfer,
  UserDetailsContactDto,
  UserDetailsDto,
  UserDetailsSalesPersonDto,
  UserPositionDto,
  UserStatus,
  UserValidateLoginDto,
  UserValidateLoginExistingUserDto,
} from '@gh/builder-shared-data';
import { cleanUserName, getDefaultEmail, getEmailLocalName } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';
import { defaults, fromPairs, omit, pick, pickBy } from 'lodash';
import { UserListItem } from './user-list-item.model';

export type UserDetailsContact = UserDetailsContactDto;
export type UserDetails = WithOptimisticLock<WithPrivilegedToken<{
  status: UserStatus;
  userEmail: string;
  sendEmailsFrom?: string;
  userId?: ID;
  targetSalespersonIds?: ID[];
  useRoundRobin?: boolean;
  userToReassignLeadsTo?: UserDetailsSalesPersonDto;
  transfer?: string;
  contact?: UserDetailsContactDto;
  role?: number;
  roleId?: ID;
  password?: string;
  position?: UserPosition;
  systemRoles: Hash<boolean>;
  confirmations?: any;
  allowAccessToLeads?: UserAllowAccess;
  leadsOf?: Hash<boolean>;
  usersFromOtherOfficesAccess: boolean;
  /** Independent office system-var gate for legacy email multi-office UI. */
  usersEmailAccessFromOtherOffices?: boolean;
  allowAccessToMultipleOffices: boolean;
  /** Legacy email-routing workaround; gates sendEmailsFrom. */
  allowEmailAccessToMultipleOffices?: boolean;
  publishToWeb: boolean;
  firstInLocation?: boolean;
  userDataToTransfer?: UserDataToTransfer;
  existingUser?:boolean;
  hasAssignedLeads?: boolean;
}>>;
export type UserValidateLogin = UserValidateLoginDto;
export type UserValidateLoginExistingUser = UserValidateLoginExistingUserDto;
export type UserPosition = UserPositionDto;

export const getNormalizedEmailName = (contact: UserDetailsContact) => {
  const firstName =  contact.firstName ? contact.firstName.split(' ')[0] : '';
  return cleanUserName(contact.firstName && contact.lastName
                         ? `${firstName}.${contact.lastName}`
                         : contact.name);
};

export function convertUserDetailsToDto(user: UserDetails, options?: { grantMode?: boolean }): UserDetailsDto {
  const omitFields = [
    'leadsOf',
    'usersFromOtherOfficesAccess',
    'usersEmailAccessFromOtherOffices',
    ...(options?.grantMode ? ['password', 'contact'] : []),
  ];
  const allowEmailAccessToMultipleOffices = Boolean(user.allowEmailAccessToMultipleOffices);
  return {
    ...<UserDetailsDto>omit(user, omitFields),
    leadsOf: Object.keys(pickBy(user.leadsOf, Boolean)),
    userEmail: getDefaultEmail(user.userEmail),
    targetSalespersonIds: user.targetSalespersonIds,
    useRoundRobin: Boolean(user.useRoundRobin),
    userDataToTransfer: user.userDataToTransfer?.userId,
    allowAccessToMultipleOffices: Boolean(user.allowAccessToMultipleOffices),
    allowEmailAccessToMultipleOffices,
    sendEmailsFrom: allowEmailAccessToMultipleOffices
      ? (getDefaultEmail(user.sendEmailsFrom || '') || null)
      : null,
  };
}

export function convertUserDetailsFromDto(
  user: UserDetailsDto,
  officeAccessFlags: {
    usersFromOtherOfficesAccess: boolean;
    usersEmailAccessFromOtherOffices: boolean;
  },
): UserDetails {
  const sendEmailsFrom = getEmailLocalName(user.sendEmailsFrom || '');
  // Coexist: prefer explicit BE flag; fall back to legacy users who already have sendEmailsFrom set.
  const allowEmailAccessToMultipleOffices = user.allowEmailAccessToMultipleOffices != null
    ? Boolean(user.allowEmailAccessToMultipleOffices)
    : Boolean(sendEmailsFrom);

  return defaults(
    {
      ...<UserDetails>omit(user, 'leadsOf'),
      leadsOf: fromPairs((user.leadsOf || []).map((leadId) => [leadId, true])),
      userEmail: getEmailLocalName(user.userEmail),
      sendEmailsFrom,
      allowEmailAccessToMultipleOffices,
      targetSalespersonIds: user.targetSalespersonIds
        || (user.userToReassignLeadsTo ? [user.userToReassignLeadsTo] : []),
      useRoundRobin: Boolean(user.useRoundRobin),
      usersFromOtherOfficesAccess: officeAccessFlags.usersFromOtherOfficesAccess,
      usersEmailAccessFromOtherOffices: officeAccessFlags.usersEmailAccessFromOtherOffices,
    },
    {
      confirmations: {
        skipChangeStatusCheck: false,
      },
    });
}

export function mergeUserDetailsWithSalesPersons(user: UserDetails, salesPersons: UserListItem[]): UserDetails {
  return { ...user, leadsOf: defaults(user.leadsOf, fromPairs(salesPersons.map((sp) => [sp.userId, false]))) };
}
